"use client"

import Image from "next/image"
import { useTranslations } from "next-intl"
import { FooterLanguageSwitcher } from "@/components/footer-language-switcher"
import { Link as I18nLink } from "@/i18n/navigation"
import { getVersion } from "@/lib/version"

interface FooterLink {
  name: string
  href: string
}

interface SiteFooterProps {
  footerLinks: {
    product: FooterLink[]
    company: FooterLink[]
    legal: FooterLink[]
  }
}

export function SiteFooter({ footerLinks }: SiteFooterProps) {
  const t = useTranslations()

  return (
    <footer className="bg-foreground text-background py-16 pb-28" role="contentinfo">
      <div className="max-w-6xl px-4 md:px-6 mx-auto">
        {/* First Row: ClubPilot section (50%) and description (50%) */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div>
            <Image
              src="/clubpilot-logo.svg"
              alt="ClubPilot"
              width={140}
              height={40}
              className="h-8 w-auto mb-4 brightness-0 invert"
              loading="lazy"
            />
          </div>
          <div>
            <p className="text-background/60 text-sm leading-relaxed">
              {t("footer.tagline")}
            </p>
          </div>
        </div>

        {/* Second Row: Menu Links (3 columns) */}
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="font-semibold mb-4">{t("footer.product")}</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.product.map((link) => {
                const isAnchorLink = link.href.startsWith("#")
                const className = "text-background/60 hover:text-background text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-foreground rounded-sm"
                
                if (isAnchorLink) {
                  return (
                    <li key={link.name}>
                      <a href={link.href} className={className}>
                        {link.name}
                      </a>
                    </li>
                  )
                }
                
                return (
                  <li key={link.name}>
                    <I18nLink
                      href={link.href as "/" | "/about" | "/impressum" | "/privacy" | "/terms" | "/gtc" | "/beta-terms" | "/waitlist"}
                      className={className}
                    >
                      {link.name}
                    </I18nLink>
                  </li>
                )
              })}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t("footer.company")}</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.company.map((link) => {
                const isAnchorLink = link.href.startsWith("#")
                const className = "text-background/60 hover:text-background text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-foreground rounded-sm"
                
                if (isAnchorLink) {
                  return (
                    <li key={link.name}>
                      <a href={link.href} className={className}>
                        {link.name}
                      </a>
                    </li>
                  )
                }
                
                return (
                  <li key={link.name}>
                    <I18nLink
                      href={link.href as "/" | "/about" | "/impressum" | "/privacy" | "/terms" | "/gtc" | "/beta-terms" | "/waitlist"}
                      className={className}
                    >
                      {link.name}
                    </I18nLink>
                  </li>
                )
              })}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t("footer.legal")}</h3>
            <ul className="space-y-2" role="list">
              {footerLinks.legal.map((link) => {
                const isAnchorLink = link.href.startsWith("#")
                const className = "text-background/60 hover:text-background text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-foreground rounded-sm"
                
                if (isAnchorLink) {
                  return (
                    <li key={link.name}>
                      <a href={link.href} className={className}>
                        {link.name}
                      </a>
                    </li>
                  )
                }
                
                return (
                  <li key={link.name}>
                    <I18nLink
                      href={link.href as "/" | "/about" | "/impressum" | "/privacy" | "/terms" | "/gtc" | "/beta-terms" | "/waitlist"}
                      className={className}
                    >
                      {link.name}
                    </I18nLink>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>

        <div className="border-t border-background/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
              <p className="text-background/60 text-sm text-center md:text-left">
                © {new Date().getFullYear()} ClubPilot. {t("footer.copyright")}
              </p>
              <span className="text-background/40 text-xs font-mono">
                v{getVersion()}
              </span>
            </div>
            <FooterLanguageSwitcher />
          </div>
        </div>
      </div>
    </footer>
  )
}
